import React, { useState, useMemo } from 'react';
import {
  DotsThree,
  Presentation,
  Pencil,
  ChartBar,
  Users,
  UserCirclePlus,
  QrCode,
} from '@phosphor-icons/react';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';

import {
  trackEditProject,
  trackClickProjectAnalytics,
} from '../../../analytics';

import { QRCode, downloadQRCode } from '../../../components/custom';
import { Dropdown } from '../../../components/general';

import { URL } from '../../../constants';
import { Event } from '../../../types';
import { formatDate, canEditEvent, getUrl } from '../../../utils';

interface Props {
  event: Event;
  onClickEvent: () => void;
  addClass?: string;
}

const ProjectBlockOverlay: React.FC<Props> = (props) => {
  const { event, onClickEvent, addClass } = props;

  const [isFocused, setIsFocused] = useState(false);

  const { eventId, checkInPageLink, isComplete } = useMemo(() => {
    return {
      eventId: event.eventId,
      checkInPageLink: getUrl(URL.CHECK_IN, event.eventId, undefined, true),
      isComplete: event.stage.toLowerCase() === 'generation',
    };
  }, [event]);

  const navigate = useNavigate();

  const items = [
    {
      icon: <Presentation />,
      label: 'Video Builder',
      onClick: onClickEvent,
    },
    {
      icon: <Pencil />,
      label: 'Edit',
      onClick: () => {
        navigate(getUrl(URL.PROJECT_SETTINGS, eventId));
        trackEditProject('Home');
      },
      show: canEditEvent(event),
    },
    {
      icon: <ChartBar />,
      label: 'Analytics',
      onClick: () => {
        navigate(getUrl(URL.PROJECT_ANALYTICS, eventId));
        trackClickProjectAnalytics('Home');
      },
      show: isComplete,
    },
    {
      icon: <Users />,
      label: 'Hub Page',
      onClick: () => window.open(getUrl(URL.SHARE, eventId), '_blank'),
      show: isComplete,
    },
    {
      icon: <UserCirclePlus />,
      label: 'Faces Check-In',
      onClick: () => window.open(checkInPageLink, '_blank'),
    },
    {
      icon: <QrCode />,
      label: 'Download Check-In QR',
      onClick: () => downloadQRCode(eventId, event.name, 'Home'),
    },
  ];

  return (
    <div
      className={classNames(
        {
          'absolute top-0 left-0 w-full h-full pointer-events-none': true,
          'sm:hidden hover:block group-hover:block focus:block focus-within:block':
            !isFocused,
          block: isFocused,
        },
        addClass,
      )}
    >
      <Dropdown
        btnData={{
          icon: <DotsThree weight="bold" className="sm:text-xl leading-none" />,
          variant: 'custom',
          colour: 'custom',
          className:
            'absolute top-2 right-2 w-8 h-8 p-0 text-black bg-white rounded-md hover:bg-primary-900 hover:text-white pointer-events-auto',
        }}
        menuItems={items
          .filter((item) => item.show !== false)
          .map((item) => ({
            label: item.label,
            icon: item.icon,
            onClick: item.onClick,
            closeOnClick: true,
            className: 'min-w-48',
            isSmall: true,
          }))}
        onOpen={() => setIsFocused(true)}
        onClose={() => setIsFocused(false)}
        position="bottom-right"
      />

      {event.startDate && event.endDate && (
        <div className="hidden sm:block absolute bottom-0 left-0 px-2 py-1.5 bg-black-overlay-600 text-xs text-white rounded-bl-md rounded-tr-md">
          {formatDate(event.startDate)} – {formatDate(event.endDate)}
        </div>
      )}

      <QRCode eventId={eventId} link={checkInPageLink} />
    </div>
  );
};

export default ProjectBlockOverlay;
